// ============================================================
// TMR Portal Admin — Crédito → Regras (rota /credito/regras) (26 §9)
// ============================================================
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Btn } from '../../components/primitives';
import { Notice } from '../../components/Notice';
import { PageHeader } from '../../components/ui';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Icons } from '../../lib/icons';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { useBusinessRules, useUpdateRuleDefault } from '../../data/businessRules/queries';
import {
  CREDIT_RATING_KEYS,
  defaultOriginLabel,
  overrideCountLabel,
  ruleRangeLabel,
  ruleReach,
  ruleValueLabel,
} from '../../data/businessRules/status';
import type { BusinessRule } from '../../data/businessRules/contract';
import { RuleValueModal } from '../businessRules/RuleValueModal';

/**
 * The four rules of the credit rating, with the TMR's default of each (26 §8). It does not duplicate Regras de
 * Negócio: it reads the same endpoints, keeps only the `vendas.rating.*` rules, and sends each clinic's exception
 * to the rule's own page, where the list of clinics lives.
 */
export function CreditRulesPage() {
  const canRead = usePermission(PERMISSIONS.businessRulesGlobalRead);
  const canManage = usePermission(PERMISSIONS.businessRulesGlobalManage);
  const query = useBusinessRules(canRead);
  const update = useUpdateRuleDefault();
  const [editing, setEditing] = useState<BusinessRule | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  if (!canRead) {
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        <div className="pa-card">
          <EmptyState message="Você não tem permissão para ver as regras de negócio da TMR." />
        </div>
      </div>
    );
  }

  const rules = (query.data?.items ?? [])
    .filter((rule) => (CREDIT_RATING_KEYS as readonly string[]).includes(rule.key))
    .sort((a, b) => CREDIT_RATING_KEYS.indexOf(a.key as never) - CREDIT_RATING_KEYS.indexOf(b.key as never));

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        eyebrow="Análise de Aprovação"
        title="Regras do rating"
        desc="O padrão da TMR para o rating de aprovação das vendas. Cada clínica pode ter a sua exceção."
      />

      {saved && <Notice tone="success">{saved}</Notice>}

      {query.isPending ? (
        <Skeleton rows={4} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : rules.length === 0 ? (
        <div className="pa-card">
          <EmptyState message="As regras do rating ainda não estão neste ambiente." />
        </div>
      ) : (
        <div className="pa-card" style={{ padding: 0, overflow: 'hidden' }}>
          {rules.map((rule) => (
            <div
              key={rule.key}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 20,
                padding: '18px 20px',
                borderBottom: '1px solid var(--border-default)',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: '1 1 360px', minWidth: 0 }}>
                <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--ink-900)' }}>{rule.name}</span>
                <p style={{ margin: '4px 0 0', fontSize: 13.5, color: 'var(--fg-muted)', lineHeight: 1.5 }}>{rule.description}</p>
                <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--fg-subtle)' }}>
                  {ruleRangeLabel(rule)} · {overrideCountLabel(rule.overrideCount)} · {defaultOriginLabel(rule)}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 500, fontSize: 16, color: 'var(--ink-900)' }}>{ruleValueLabel(rule, rule.defaultValue)}</span>
                {canManage && (
                  <Btn variant="secondary" size="sm" icon={Icons.pen} onClick={() => setEditing(rule)}>
                    Alterar padrão
                  </Btn>
                )}
                <Link to={`/regras-negocio/${encodeURIComponent(rule.key)}`} className="pa-link-btn" style={{ fontSize: 13.5, textDecoration: 'none' }}>
                  Exceções por clínica
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <RuleValueModal
          title="Alterar o padrão"
          rule={editing}
          initialValue={editing.defaultValue}
          reach={ruleReach(editing, 'default')}
          confirmLabel="Salvar padrão"
          onSave={async (value) => {
            const rule = await update.mutateAsync({ key: editing.key, value });
            setSaved(`${rule.name}: o padrão agora é ${ruleValueLabel(rule, rule.defaultValue)}.`);
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
